/**
 * lib/auth/accountProfile.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Sprint: Auth Foundation V1 — minimal account profile (public.profiles).
 *
 * Deliberately a SEPARATE module from lib/auth/profileSync.ts, which reads/
 * writes public.traveler_profiles / public.journey_history — Planner-domain
 * tables that do NOT exist in production (PROFILE_SYNC_ENABLED = false there;
 * see that file's own doc). public.profiles is a NEW, real, minimal table
 * this sprint creates via supabase/migrations/020_profiles_table.sql: id,
 * email, display_name, preferred_language, preferred_currency, created_at,
 * updated_at. RLS-scoped to auth.uid() = id; the frontend only ever uses
 * the anon-key client + the user's own session, never a service role.
 *
 * Sprint: Account UX Refinement V1 — supabase/migrations/022_profiles_
 * completion_fields.sql added 4 more OPTIONAL columns (phone, home_country,
 * preferred_traveler_type, travel_interests) so /cuenta's Perfil/Preferencias
 * de viaje tabs can invite the user to complete something travel-useful.
 * Every one of these is nullable/defaulted — never required to use the
 * account page. Never a sensitive field (no passport/ID/birthdate/payment).
 *
 * Every function here is silent-safe: never throws, never logs a raw email/
 * token/error message (error TYPE only, when logged at all), and resolves
 * to null/false on any failure so a caller can treat sync as purely
 * best-effort — a failed profile read/write must never block the
 * marketplace or the account page from rendering with local-only data.
 */

import { supabase } from "@/lib/supabase";

/** Sprint: Account UX Refinement V1 — same 5 traveler-type values as the
 *  DB's own profiles_preferred_traveler_type_check constraint. */
export type PreferredTravelerType = "solo" | "pareja" | "familia" | "amigos" | "mayores";

/** Sprint: Account UX Refinement V1 — the same canonical filter-taxonomy
 *  group keys engine/supply_integration/marketplace_filter_taxonomy.py
 *  already defines (art_culture, history_heritage, ...). A PROFILE
 *  PREFERENCE only — storing a value here never changes marketplace
 *  search/filter behavior in this sprint. */
export const TRAVEL_INTEREST_KEYS = [
  "art_culture", "history_heritage", "nature_outdoors", "beach_water",
  "food_drink", "local_life", "museums_tickets", "adventure",
  "family_kids", "romance", "nightlife", "free_tours", "transfers_airport",
] as const;
export type TravelInterestKey = typeof TRAVEL_INTEREST_KEYS[number];

export interface AccountProfile {
  id:                       string;
  email:                    string;
  display_name:             string | null;
  preferred_language:       string;
  preferred_currency:       string;
  phone:                    string | null;
  home_country:             string | null;
  preferred_traveler_type:  PreferredTravelerType | null;
  travel_interests:         string[];
  created_at:               string;
  updated_at:               string;
}

const PROFILE_COLUMNS = "id, email, display_name, preferred_language, preferred_currency, phone, home_country, preferred_traveler_type, travel_interests, created_at, updated_at";

/**
 * Reads the current user's profile row. Returns null when Supabase isn't
 * configured, the row doesn't exist yet, or the request fails for any
 * reason (RLS denial, network, etc.) — never throws.
 */
export async function loadAccountProfile(userId: string): Promise<AccountProfile | null> {
  if (!supabase) return null;
  try {
    const { data } = await supabase
      .from("profiles")
      .select(PROFILE_COLUMNS)
      .eq("id", userId)
      .maybeSingle();
    if (!data) return null;
    // travel_interests is jsonb server-side — normalize to a plain string[]
    // even if a row somehow has null/malformed data, rather than trust the
    // DB's own array-shape CHECK constraint blindly on the read path too.
    const row = data as AccountProfile;
    return { ...row, travel_interests: Array.isArray(row.travel_interests) ? row.travel_interests : [] };
  } catch {
    return null;
  }
}

/**
 * Sprint: Account UX Refinement V1 — how many of the optional, travel-useful
 * profile fields are filled in. Deliberately excludes preferred_language/
 * preferred_currency (they always have a value via DB defaults, so they'd
 * never read as "incomplete") and email/id (never optional in the first
 * place). Pure function — no network, safe to call on every render.
 */
export interface ProfileCompletion {
  completedCount: number;
  totalCount:     number;
  percent:        number;
}

export function computeProfileCompletion(profile: AccountProfile | null): ProfileCompletion {
  const totalCount = 5;
  if (!profile) return { completedCount: 0, totalCount, percent: 0 };
  const checks = [
    Boolean(profile.display_name && profile.display_name.trim().length > 0),
    Boolean(profile.phone && profile.phone.trim().length > 0),
    Boolean(profile.home_country && profile.home_country.trim().length > 0),
    Boolean(profile.preferred_traveler_type),
    Array.isArray(profile.travel_interests) && profile.travel_interests.length > 0,
  ];
  const completedCount = checks.filter(Boolean).length;
  return { completedCount, totalCount, percent: Math.round((completedCount / totalCount) * 100) };
}

/**
 * Ensures a profile row exists for this user, creating one seeded from
 * their current session email + local preferences on first login. Returns
 * the existing row unchanged when one already exists — this is a seed-once
 * operation, never an overwrite of a returning user's own saved prefs.
 * Best-effort: returns null on any failure, never throws.
 */
export async function ensureAccountProfile(
  userId:            string,
  email:             string,
  preferredLanguage: string,
  preferredCurrency: string,
): Promise<AccountProfile | null> {
  if (!supabase) return null;
  try {
    const existing = await loadAccountProfile(userId);
    if (existing) return existing;

    const { data, error } = await supabase
      .from("profiles")
      .insert({
        id:                 userId,
        email,
        preferred_language: preferredLanguage,
        preferred_currency: preferredCurrency,
      })
      .select(PROFILE_COLUMNS)
      .maybeSingle();
    if (error) {
      console.warn("[accountProfile] ensureAccountProfile insert failed:", error.code ?? "unknown");
      return null;
    }
    if (!data) return null;
    const row = data as AccountProfile;
    return { ...row, travel_interests: Array.isArray(row.travel_interests) ? row.travel_interests : [] };
  } catch {
    return null;
  }
}

/**
 * Best-effort update of the user's own profile row — preferences (Auth
 * Foundation V1) AND the Account UX Refinement V1 profile-completion
 * fields (phone/home_country/preferred_traveler_type/travel_interests),
 * all optional. Never blocks/throws — a failed save leaves local state
 * (preferences store / the caller's own form state) completely unaffected;
 * the caller decides how to surface the failure (see routes/cuenta.tsx's
 * own save status handling).
 */
export interface AccountProfileUpdates {
  preferred_language?:      string;
  preferred_currency?:      string;
  display_name?:            string | null;
  phone?:                   string | null;
  home_country?:            string | null;
  preferred_traveler_type?: PreferredTravelerType | null;
  travel_interests?:        string[];
}

export async function updateAccountPreferences(
  userId:  string,
  updates: AccountProfileUpdates,
): Promise<boolean> {
  if (!supabase) return false;
  try {
    const { error } = await supabase.from("profiles").update(updates).eq("id", userId);
    return !error;
  } catch {
    return false;
  }
}
